import React, { useMemo, useRef, useState } from 'react';
import { Film, Music } from 'lucide-react';
import { ChipMenu, type ChipOption } from './Composer';
import { PromptHighlights, type HighlightSpan } from './PromptHighlights';
import { FileImage } from '../../FileImage';
import {
    filterMentionCandidates, findMentionAtCaret, findUnknownReferenceTags, insertMention, insertTagAt,
    referenceTagAt, referenceTagSpans, replaceTag,
    type MentionCandidate, type MentionToken, type TagSpan,
} from '../../../utils/promptMentions';

/**
 * Tags in a prompt (@Image1, @Video1, @Audio1) for the Image and Video nodes, the same in both:
 * the tags marked in place as soft pills, a click on one opening the references to swap it for,
 * the list that opens as @ is typed, and the tags that name nothing attached.
 */

/** Something the prompt can name, in the order it is sent */
export interface PromptTagRef {
    tag: string;
    label: string;
    kind: 'image' | 'video' | 'audio';
    url: string;
}

/**
 * A reference's tag (@Image1, @Audio1) as something to press, which puts it into the prompt.
 * The one the arrow keys are on, in the list that opens at an @, is in the node's tone.
 */
export const REF_TAG = 'shrink-0 rounded-full ring-1 font-semibold';
export const REF_TAG_IDLE = 'bg-white ring-cream-300 text-ink hover:ring-lavender';
export const REF_TAG_ACTIVE = 'bg-[color:var(--tone-soft)] ring-[color:var(--tone-line)] text-[color:var(--tone-deep)]';

interface PromptTagsOptions {
    prompt: string;
    /** What the prompt can name; none turns the tags off */
    refs: PromptTagRef[];
    fieldRef: React.RefObject<HTMLTextAreaElement>;
    /** A prompt changed by a tag put in or swapped */
    onPrompt: (value: string) => void;
    userId?: string;
    workspaceName?: string;
}

export function usePromptTags({ prompt, refs, fieldRef, onPrompt, userId, workspaceName }: PromptTagsOptions) {
    const [mention, setMention] = useState<MentionToken | null>(null);
    const [mentionIndex, setMentionIndex] = useState(0);
    // A tag in the prompt that was clicked: the references, to swap it for another
    const [swap, setSwap] = useState<(TagSpan & { anchor: HTMLElement }) | null>(null);
    const highlightsRef = useRef<HTMLDivElement | null>(null);
    const enabled = refs.length > 0;

    const candidates = useMemo<MentionCandidate[]>(
        () => (mention && enabled ? filterMentionCandidates(mention.query, refs.map(ref => ref.label), i => refs[i].tag) : []),
        [mention, enabled, refs],
    );
    const unknownTags = useMemo(
        () => (enabled ? findUnknownReferenceTags(prompt, refs.map(ref => ref.tag)) : []),
        [enabled, prompt, refs],
    );
    // The tags in the prompt, marked in place: in the node's colour, or as a problem when they name nothing attached
    const spans = useMemo<HighlightSpan[]>(() => {
        if (!enabled) return [];
        const known = new Set(refs.map(ref => ref.tag.toLowerCase()));
        return referenceTagSpans(prompt).map(span => ({ ...span, known: known.has(span.tag.toLowerCase()) }));
    }, [enabled, prompt, refs]);
    const swapOptions = useMemo<ChipOption[]>(() => refs.map(ref => ({
        value: ref.tag,
        label: ref.label,
        hint: ref.tag,
        icon: ref.kind === 'video' ? <Film /> : ref.kind === 'audio' ? <Music />
            : <FileImage src={ref.url} userId={userId} workspaceName={workspaceName} className="w-5 h-5 rounded-md object-cover" />,
    })), [refs, userId, workspaceName]);

    const updateMention = (el: HTMLTextAreaElement) => {
        if (!enabled) { if (mention) setMention(null); return; }
        const next = findMentionAtCaret(el.value, el.selectionStart ?? el.value.length);
        setMention(prev => (prev?.start === next?.start && prev?.query === next?.query ? prev : next));
        if (!next) setMentionIndex(0);
    };

    const applyPrompt = (value: string, caret: number) => {
        onPrompt(value);
        setMention(null);
        setMentionIndex(0);
        requestAnimationFrame(() => {
            const el = fieldRef.current;
            if (el) { el.focus(); el.setSelectionRange(caret, caret); }
        });
    };

    /** A click in the prompt on a tag opens the references to swap it for; a click elsewhere is a click */
    const openSwapAtCaret = (el: HTMLTextAreaElement): boolean => {
        if (!enabled) return false;
        const caret = el.selectionStart ?? 0;
        if (caret !== (el.selectionEnd ?? caret)) return false; // a selection is being made
        const span = referenceTagAt(el.value, caret);
        const anchor = span && highlightsRef.current?.querySelector<HTMLElement>(`[data-tag-start="${span.start}"]`);
        if (!span || !anchor) return false;
        setMention(null);
        setSwap({ ...span, anchor });
        return true;
    };
    const swapTag = (tag: string) => {
        if (!swap) return;
        const res = replaceTag(prompt, swap, tag);
        setSwap(null);
        applyPrompt(res.value, res.caret);
    };
    const closeSwap = () => { setSwap(null); fieldRef.current?.focus({ preventScroll: true }); };

    const insertCandidate = (c: MentionCandidate) => {
        const el = fieldRef.current;
        if (!el || !mention) return;
        const res = insertMention(el.value, mention, el.selectionStart ?? el.value.length, c.tag);
        applyPrompt(res.value, res.caret);
    };

    /** Puts a tag at the caret, or at the end when the prompt is not being edited. */
    const insertTag = (tag: string) => {
        const el = fieldRef.current;
        const caret = el && document.activeElement === el ? (el.selectionStart ?? prompt.length) : prompt.length;
        const res = insertTagAt(prompt, caret, tag);
        applyPrompt(res.value, res.caret);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (!mention || candidates.length === 0) return;
        const count = candidates.length;
        if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => (i + 1) % count); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex(i => (i - 1 + count) % count); }
        else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertCandidate(candidates[Math.min(mentionIndex, count - 1)]); }
        else if (e.key === 'Escape') { e.preventDefault(); setMention(null); }
    };

    /** What the prompt field takes; its onChange calls `onFieldChange` after saving the prompt */
    const fieldProps = {
        onKeyDown,
        onKeyUp: (e: React.KeyboardEvent<HTMLTextAreaElement>) => updateMention(e.currentTarget),
        onClick: (e: React.MouseEvent<HTMLTextAreaElement>) => { if (!openSwapAtCaret(e.currentTarget)) updateMention(e.currentTarget); },
        onBlur: () => { window.setTimeout(() => setMention(null), 150); },
        'aria-autocomplete': enabled ? 'list' as const : undefined,
        'aria-expanded': enabled ? mention !== null && candidates.length > 0 : undefined,
        title: spans.length > 0 ? 'Click a tag to change which reference it names' : undefined,
    };

    return {
        enabled, refs, prompt, fieldRef, highlightsRef, spans, swap, swapOptions, swapTag, closeSwap,
        mention, mentionIndex, candidates, insertCandidate, unknownTags, insertTag,
        fieldProps, onFieldChange: updateMention, userId, workspaceName,
    };
}

export type PromptTags = ReturnType<typeof usePromptTags>;

/**
 * The pills behind the prompt field and the menu of a clicked tag. Rendered after the field, so
 * the field is there to measure; it lies behind the field by its layer (the field is `relative z-[1]`).
 */
export const PromptTagLayer: React.FC<{ tags: PromptTags }> = ({ tags }) => (
    <>
        {tags.spans.length > 0 && (
            <PromptHighlights ref={tags.highlightsRef} fieldRef={tags.fieldRef} value={tags.prompt} spans={tags.spans} activeStart={tags.swap?.start ?? null} />
        )}
        {tags.swap && (
            <ChipMenu
                anchor={tags.swap.anchor}
                label={`Change ${tags.swap.tag}`}
                options={tags.swapOptions}
                value={tags.swap.tag}
                onPick={tags.swapTag}
                onClose={tags.closeSwap}
            />
        )}
    </>
);

/** The list that opens as @ is typed: in the pinned area, so it is seen however far the prompt is scrolled */
export const PromptTagPicker: React.FC<{ tags: PromptTags }> = ({ tags }) => {
    if (!tags.mention || tags.candidates.length === 0) return null;
    return (
        <div role="listbox" aria-label="Insert a reference" className="shrink-0 px-3 py-2 border-t border-cream-200 bg-cream-50 flex flex-wrap items-center gap-1.5">
            <span className="ns-t-label mr-0.5">Tag</span>
            {tags.candidates.map((c, i) => {
                const ref = tags.refs[c.index];
                return (
                    <button
                        key={c.index}
                        type="button"
                        role="option"
                        aria-selected={i === tags.mentionIndex}
                        title={c.label}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => tags.insertCandidate(c)}
                        className={`flex items-center gap-1.5 pl-0.5 pr-2 py-0.5 text-2xs ${REF_TAG} ${i === tags.mentionIndex ? REF_TAG_ACTIVE : REF_TAG_IDLE}`}
                    >
                        <span className="w-5 h-5 rounded-full overflow-hidden bg-cream-200 shrink-0 grid place-items-center text-[color:var(--tone-deep)]">
                            {ref?.kind === 'video' ? <Film size={12} />
                                : ref?.kind === 'audio' ? <Music size={12} />
                                    : <FileImage src={ref?.url} userId={tags.userId} workspaceName={tags.workspaceName} className="w-full h-full object-cover" />}
                        </span>
                        {c.tag}
                    </button>
                );
            })}
            <span className="ns-t-meta ml-auto">↑↓ · Enter</span>
        </div>
    );
};
